import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
}

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    container: {
        gap: 5,
        css: css`
            > ${GridOffset} {
                margin-top: 0px;
                margin-bottom: 0px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        > div {
                            display: block !important;
                        }
                    `,
                    css`
                        > div {
                            display: block !important;
                        }
                    `,
                    css`                    
                    `,
                    css`
                    `,
                    null,
                ])}
        `,
    },
    erpNumberGridItem: {
        css: css`
            flex-basis: auto;
            margin-right: 15px !important;
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    manufacturerItemGridItem: {
        css: css`
            flex-basis: auto;
        `,
    },
    erpNumberLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    erpNumberValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    customerNameLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    customerNameValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    manufacturerItemLabelText: {
        weight: "bold",
        css: css`
            flex: 0 0 auto;
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            flex: 1 1 auto;
            min-width: 0;
            ${wrapInContainerStyles}
            font-size: 14px;
            line-height: 17px;
            color: ${getColor("text.accent")};
            font-weight: bold;
            font-family: "Nunito Sans", sans-serif;
        `,
    },
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}  data-test-selector="productPartNumbers">
            <GridItem width={gridItemWidth as GridWidths} {...styles.erpNumberGridItem}>
                {showLabel && <Typography {...styles.erpNumberLabelText}>{translate("ADI # ")}</Typography>}
                {!showLabel && (
                    <VisuallyHidden>
                        <Typography {...styles.erpNumberLabelText}>{translate("ADI # ")}</Typography>
                    </VisuallyHidden>
                )}
                <Typography {...styles.erpNumberValueText} data-test-selector="productNumber">
                    {productNumber}
                </Typography>
            </GridItem>
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.customerNameGridItem}>
                    {showLabel && <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.customerNameLabelText}>{translate("My Part #")}</Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.customerNameValueText} data-test-selector="customerProductNumber">{customerProductNumber}</Typography>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.manufacturerItemGridItem}>
                    {showLabel && (
                        <Typography {...styles.manufacturerItemLabelText}>{translate("MODEL # ")}</Typography>
                    )}
                    {!showLabel && (
                        <VisuallyHidden>
                            <Typography {...styles.manufacturerItemLabelText}>{translate("MODEL # ")} </Typography>
                        </VisuallyHidden>
                    )}
                    <Typography {...styles.manufacturerItemValueText} data-test-selector="manufacturerItem">
                        {translate}{manufacturerItem}{translate}
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default ProductPartNumbers;

